import React, { useState, useEffect } from "react";
import { getFirestore, collection, query, where, getDocs } from "firebase/firestore";
import { formatSubscriptionDate, getPlanDetails, getDaysRemaining } from "../utils/premiumHelpers";
import firebaseConfig from "../firebaseConfig";

export default function AdminSubscriptionsList() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("active"); // active, expired, all
  const [sortBy, setSortBy] = useState("endDate"); // endDate, plan, amount
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const appId = firebaseConfig.appId;

      // Fetch all user profiles with active or inactive subscriptions
      const profilesQuery = query(
        collection(db, `artifacts/${appId}/public/data/user_profiles`),
        where("subscriptionStatus", "!=", "none")
      );

      const querySnapshot = await getDocs(profilesQuery);
      const subs = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setSubscriptions(subs);
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
      setError("Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAndSorted = () => {
    let filtered = subscriptions;

    // Filter by status
    if (filterStatus !== "all") {
      filtered = filtered.filter((sub) => sub.subscriptionStatus === filterStatus);
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (sub) =>
          sub.name?.toLowerCase().includes(search) ||
          sub.email?.toLowerCase().includes(search) ||
          sub.userId.includes(search)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === "endDate") {
        const aDate = a.subscriptionEndDate?.toMillis?.() || 0;
        const bDate = b.subscriptionEndDate?.toMillis?.() || 0;
        return aDate - bDate;
      } else if (sortBy === "plan") {
        return a.subscriptionPlan?.localeCompare(b.subscriptionPlan || "");
      } else if (sortBy === "amount") {
        return (b.subscriptionAmount || 0) - (a.subscriptionAmount || 0);
      }
      return 0;
    });

    return filtered;
  };

  const filteredSubs = getFilteredAndSorted();
  const activeCount = subscriptions.filter((s) => s.subscriptionStatus === "active").length;
  const expiredCount = subscriptions.filter((s) => s.subscriptionStatus === "expired").length;
  const totalRevenue = subscriptions.reduce((sum, s) => sum + (s.subscriptionAmount || 0), 0);

  if (loading) return <div className="text-center py-8">Loading subscriptions...</div>;
  if (error) return <div className="text-center py-8 text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-sm text-green-600 dark:text-green-400 font-semibold">Active Subscriptions</p>
          <p className="text-3xl font-bold text-green-800 dark:text-green-300 mt-2">{activeCount}</p>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-600 dark:text-red-400 font-semibold">Expired Subscriptions</p>
          <p className="text-3xl font-bold text-red-800 dark:text-red-300 mt-2">{expiredCount}</p>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-600 dark:text-blue-400 font-semibold">Total Users</p>
          <p className="text-3xl font-bold text-blue-800 dark:text-blue-300 mt-2">{subscriptions.length}</p>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
          <p className="text-sm text-purple-600 dark:text-purple-400 font-semibold">Total Revenue</p>
          <p className="text-3xl font-bold text-purple-800 dark:text-purple-300 mt-2">₹{totalRevenue.toLocaleString()}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-700 p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Search (Name/Email/ID)
            </label>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Filter by Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Sort by
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
            >
              <option value="endDate">Expiry Date</option>
              <option value="plan">Plan</option>
              <option value="amount">Amount Paid</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-700 rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 dark:bg-gray-600 border-b border-gray-300 dark:border-gray-500">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-800 dark:text-white">User</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-800 dark:text-white">Email</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-800 dark:text-white">Plan</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-800 dark:text-white">Amount</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-800 dark:text-white">Expires</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-800 dark:text-white">Days Left</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-800 dark:text-white">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
            {filteredSubs.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-4 text-center text-gray-600 dark:text-gray-400">
                  No subscriptions found
                </td>
              </tr>
            ) : (
              filteredSubs.map((sub) => {
                const planDetails = getPlanDetails(sub.subscriptionPlan);
                const daysLeft = getDaysRemaining(sub.subscriptionEndDate);
                const isExpiringSoon = daysLeft <= 10 && daysLeft > 0;

                return (
                  <tr key={sub.id} className="hover:bg-gray-50 dark:hover:bg-gray-600 transition">
                    <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white">
                      {sub.name || "N/A"}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{sub.email || "N/A"}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {planDetails?.name || "Unknown"}
                    </td>
                    <td className="px-4 py-3 font-semibold text-green-600 dark:text-green-400">
                      ₹{sub.subscriptionAmount?.toLocaleString() || 0}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {formatSubscriptionDate(sub.subscriptionEndDate)}
                    </td>
                    <td className={`px-4 py-3 font-bold ${isExpiringSoon ? "text-red-600 dark:text-red-400" : daysLeft <= 0 ? "text-red-800 dark:text-red-300" : "text-green-600 dark:text-green-400"}`}>
                      {daysLeft > 0 ? daysLeft : "Expired"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                          sub.subscriptionStatus === "active"
                            ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
                            : sub.subscriptionStatus === "expired"
                              ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300"
                              : "bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300"
                        }`}
                      >
                        {sub.subscriptionStatus?.charAt(0).toUpperCase() + sub.subscriptionStatus?.slice(1)}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="text-center text-sm text-gray-600 dark:text-gray-400">
        Showing {filteredSubs.length} of {subscriptions.length} subscriptions
      </div>
    </div>
  );
}
